import { useCallback, useMemo } from 'react';
import { useGraphicUploader } from 'components/common/graphicUploader/useLocalGraphicUploader';
import { ATTACHMENTS_UPLOADER_PRESET } from 'components/common/graphicUploader/attachmentsPreset';
import {
  useDocumentDeepLinkActive,
  useProtocolSectionDocumentRouteSync,
  useSectionTemplateDocumentRouteSync,
} from 'components/common/graphicUploader/documentRouteSync';
import {
  useInspection,
  useInspectionTemplate,
  useInspectionStore,
  useInspectionFieldDisabled,
  isInspectionFieldDisabled,
} from 'store/inspection';
import {
  createInspectionSectionRoute,
  createInspectionTemplateSectionRoute,
  createProtocolSectionDocumentsBaseUrl,
} from 'views/inspections/single/helpers';
import { createTemplateChecklistSectionDocumentsBaseUrl } from 'shared/domain/template/templateDocumentPaths';
import {
  DocumentToProtocolSectionFromUrlBinder,
  DocumentToSectionBinder,
  IDocumentBinder,
} from 'shared/domain/document/documentBinder';
import { getDocumentScopeKey } from 'shared/domain/document/documentScope';
import { SectionTreeNode } from 'shared/domain/checklistTree';
import {
  useAppUrlParams,
  UrlParamName,
} from 'hooks/navigation/useAppUrlParams';
export const PROTOCOL_SECTION_UPLOADER_PREFIX = 'protocol-section-uploader';

interface UseInspectionSectionUploadersParams {
  node: SectionTreeNode;
  readOnly: boolean;
  enableAtomicChanges?: boolean;
}

export function useInspectionSectionUploaders({
  node,
  readOnly,
  enableAtomicChanges = false,
}: UseInspectionSectionUploadersParams) {
  const projectId = useAppUrlParams(UrlParamName.PROJECT_ID);
  const { inspection, inspectionDocuments } = useInspection();
  const { inspectionTemplate, inspectionTemplateDocuments } =
    useInspectionTemplate();
  const atomicSaveEnabled = useInspectionStore.use.atomicSaveEnabled();
  const inspectionId = inspection?._id;
  const templateId = inspectionTemplate?._id ?? '';

  const protocolSection = useMemo(() => {
    return inspection?.sections?.find(
      (section) => section.checklistSectionId === node.id
    );
  }, [inspection?.sections, node.id]);

  const getTemplateSectionBaseUrl = useCallback(
    () =>
      createInspectionTemplateSectionRoute(
        projectId,
        inspectionId ?? '',
        templateId,
        node.id
      ),
    [inspectionId, node.id, projectId, templateId]
  );

  const templateSectionRouteSync = useSectionTemplateDocumentRouteSync(
    inspectionId,
    getTemplateSectionBaseUrl,
    templateId,
    node.id
  );

  const shouldShowTemplateUploaderForRoute = useDocumentDeepLinkActive(
    templateSectionRouteSync.autoOpenCheck,
    templateSectionRouteSync.internalEntityBaseUrl
  );

  const getProtocolSectionBaseUrl = useCallback(
    () =>
      createInspectionSectionRoute(
        projectId,
        inspectionId ?? '',
        protocolSection?._id ?? ''
      ),
    [inspectionId, projectId, protocolSection?._id]
  );

  const protocolSectionRouteSync = useProtocolSectionDocumentRouteSync(
    inspectionId,
    getProtocolSectionBaseUrl,
    protocolSection?._id
  );

  const shouldShowProtocolUploaderForRoute = useDocumentDeepLinkActive(
    protocolSectionRouteSync.autoOpenCheck,
    protocolSectionRouteSync.internalEntityBaseUrl
  );

  const templateSectionDocuments =
    inspectionTemplateDocuments.sectionDocuments[node.id];

  const templateSectionUploaderConfig = useMemo(
    () => ({
      baseUrl: createTemplateChecklistSectionDocumentsBaseUrl(
        templateId,
        node.id
      ),
      documentScopeKey: getDocumentScopeKey({
        ...(inspectionId ? { inspectionId } : {}),
        templateId,
      }),
      previewOnly: true as const,
      internalEntityBaseUrl: templateSectionRouteSync.internalEntityBaseUrl,
      autoOpenCheck: templateSectionRouteSync.autoOpenCheck,
      documentBinderFactory: (): IDocumentBinder =>
        new DocumentToSectionBinder({
          templateId,
          sectionId: node.id,
          ...(inspectionId ? { inspectionId } : {}),
        }),
    }),
    [
      inspectionId,
      node.id,
      templateId,
      templateSectionRouteSync.autoOpenCheck,
      templateSectionRouteSync.internalEntityBaseUrl,
    ]
  );

  const templateGraphicUploader = useGraphicUploader(
    templateSectionDocuments,
    templateSectionUploaderConfig
  );

  const protocolDocuments = protocolSection?._id
    ? inspectionDocuments[protocolSection._id]
    : undefined;

  const protocolSectionUploaderConfig = useMemo(
    () => ({
      ...ATTACHMENTS_UPLOADER_PRESET,
      attachmentDropTarget: 'inspection' as const,
      baseUrl: createProtocolSectionDocumentsBaseUrl(
        inspectionId || '',
        protocolSection?._id || ''
      ),
      atomicChanges: enableAtomicChanges && atomicSaveEnabled && !readOnly,
      previewOnly: readOnly,
      dataQaPrefix: `protocol-section-${node.id}-`,
      internalEntityBaseUrl: protocolSectionRouteSync.internalEntityBaseUrl,
      autoOpenCheck: protocolSectionRouteSync.autoOpenCheck,
      documentBinderFactory: (baseUrl?: string) => {
        if (!baseUrl) throw new Error('Section url binder requires URL');
        return new DocumentToProtocolSectionFromUrlBinder(baseUrl);
      },
    }),
    [
      atomicSaveEnabled,
      enableAtomicChanges,
      inspectionId,
      protocolSection?._id,
      protocolSectionRouteSync.autoOpenCheck,
      protocolSectionRouteSync.internalEntityBaseUrl,
      readOnly,
    ]
  );

  const graphicUploader = useGraphicUploader(
    protocolDocuments,
    protocolSectionUploaderConfig
  );

  return {
    templateGraphicUploader,
    graphicUploader,
    uploaderKey: `${PROTOCOL_SECTION_UPLOADER_PREFIX}-${node.id}`,
    shouldShowTemplateUploaderForRoute,
    shouldShowProtocolUploaderForRoute,
  };
}

export function useInspectionSectionReadOnly(): boolean {
  const resolveFieldDisabled = useInspectionFieldDisabled();
  return isInspectionFieldDisabled(resolveFieldDisabled(false));
}
